/*
 * Owns one Box exclusion list's draft so the add and edit modals share the same
 * add-time validation and pending-entry bookkeeping instead of each re-implementing it.
 * Validation results stay as raw discriminants; the editor localizes them for display.
 */
import { useState } from 'react';
import { type ExclusionEntry } from 'src/utils/exclusionEntries';

export type ExclusionEntryValidator = (value: string, existing: readonly string[]) => string | null;

/** Turns what the user typed into the entry value that is stored; the typed text is kept by default. */
export type ExclusionEntryComposer = (input: string) => string;

export interface ExclusionListDraft {
  readonly entries: readonly ExclusionEntry[];
  readonly values: readonly string[];
  readonly input: string;
  readonly validation: string | null;
  readonly setInput: (value: string) => void;
  readonly add: () => void;
  readonly remove: (index: number) => void;
  /** Commits a typed-but-unadded entry; returns `null` when that entry is invalid. */
  readonly resolvePending: () => readonly string[] | null;
  /** Restores the entries the hook was first given and clears the pending input and its error. */
  readonly reset: () => void;
}

const toValues = (entries: readonly ExclusionEntry[]): string[] => entries.map(({ value }) => value);

const keepInput: ExclusionEntryComposer = (input) => input;

export const useExclusionListDraft = (
  validate: ExclusionEntryValidator,
  initialEntries: readonly ExclusionEntry[] = [],
  composeValue: ExclusionEntryComposer = keepInput,
): ExclusionListDraft => {
  // Snapshotted once so reset() restores the opening list even if the caller's seed changes later.
  const [seedEntries] = useState<readonly ExclusionEntry[]>(() => [...initialEntries]);
  const [entries, setEntries] = useState<readonly ExclusionEntry[]>(seedEntries);
  const [input, setInputValue] = useState('');
  const [validation, setValidation] = useState<string | null>(null);
  const values = toValues(entries);

  const commitInput = (): readonly string[] | null => {
    const result = validate(input, values);
    if (result !== null) {
      setValidation(result);
      return null;
    }
    const next = [...entries, { value: composeValue(input), isAdvanced: false }];
    setEntries(next);
    setInputValue('');
    setValidation(null);
    return toValues(next);
  };

  return {
    entries,
    values,
    input,
    validation,
    setInput: (value) => {
      setInputValue(value);
      setValidation(null);
    },
    add: () => { commitInput(); },
    remove: (index) => { setEntries(entries.filter((_, itemIndex) => itemIndex !== index)); },
    resolvePending: () => (input === '' ? values : commitInput()),
    reset: () => {
      setEntries(seedEntries);
      setInputValue('');
      setValidation(null);
    },
  };
};
